import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '../supabase.js';
import { Icon } from './Icon.js';
import ReactECharts from 'echarts-for-react';
import JellyfishLoader from './JellyfishLoader';
import wearableService from '../services/WearableService';
import { dedupeRequest, getCacheKey } from '../utils/queryDeduplication';
import { getUserFacingErrorMessage } from '../utils/sanitizeErrorForLog';
import './WearablesTab.css';

// Sleep Sparkline Chart Component (similar to ProtocolReport style)
const SleepSparkline = ({ data }) => {
  if (!data || data.length === 0) {
    return <svg className="sleep-sparkline" viewBox="0 0 120 40" preserveAspectRatio="none" />;
  }

  const width = 140;
  const height = 36;
  const padding = 6;

  // Convert weekly data to points (using day index as x, total sleep as y)
  const points = data.map((d, index) => ({
    x: index,
    y: d.total || 0
  }));

  // Domain for Y (sleep hours) - use dynamic range with padding to show variation better
  const values = points.map(p => p.y).filter(v => v > 0); // Filter out zeros for better scaling
  if (values.length === 0) {
    return <svg className="sleep-sparkline" viewBox="0 0 120 40" preserveAspectRatio="none" />;
  }
  
  const dataMin = Math.min(...values);
  const dataMax = Math.max(...values);
  const range = dataMax - dataMin;
  
  // Add 15% padding above and below to make variations more visible
  const yAxisPadding = Math.max(range * 0.15, 0.5); // At least 0.5 hours padding
  const minY = Math.max(0, dataMin - yAxisPadding);
  const maxY = dataMax + yAxisPadding;

  const scaleX = (x) => {
    const maxX = points.length - 1;
    if (maxX === 0) return padding + (width - 2 * padding) / 2;
    return padding + (x / maxX) * (width - 2 * padding);
  };

  const scaleY = (y) => {
    if (maxY === minY) return height / 2;
    // Invert Y for SVG
    return padding + (1 - (y - minY) / (maxY - minY)) * (height - 2 * padding);
  };

  // Create smooth path through points
  const scaledPoints = points.map(p => ({
    x: scaleX(p.x),
    y: scaleY(p.y)
  }));

  const smoothing = 0.2;
  const controlPoint = (current, previous, next, reverse) => {
    const p = previous || current;
    const n = next || current;
    const o = {
      length: Math.hypot(n.x - p.x, n.y - p.y),
      angle: Math.atan2(n.y - p.y, n.x - p.x)
    };
    const angle = o.angle + (reverse ? Math.PI : 0);
    const length = o.length * smoothing;
    return {
      x: current.x + Math.cos(angle) * length,
      y: current.y + Math.sin(angle) * length
    };
  };

  const pathD = scaledPoints.reduce((acc, point, i, a) => {
    if (i === 0) return `M ${point.x} ${point.y}`;
    const cps = controlPoint(a[i - 1], a[i - 2], point, false);
    const cpe = controlPoint(point, a[i - 1], a[i + 1], true);
    return `${acc} C ${cps.x} ${cps.y}, ${cpe.x} ${cpe.y}, ${point.x} ${point.y}`;
  }, '');

  return (
    <svg className="sleep-sparkline" viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none">
      <path d={pathD} stroke="rgba(255, 255, 255, 0.9)" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
};

function WearablesTab({ userId: userIdProp, isOwnProfile = true }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [showInputModal, setShowInputModal] = useState(false);
  const [showSleepDetailModal, setShowSleepDetailModal] = useState(false);
  const [inputData, setInputData] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [userId, setUserId] = useState(userIdProp);
  const [sleepSessions, setSleepSessions] = useState([]);
  const [sleepDetailDateRange, setSleepDetailDateRange] = useState({
    startDate: '',
    endDate: ''
  });
  const [sleepDetailShowPercentage, setSleepDetailShowPercentage] = useState(false);
  const [selectedAnalysisDate, setSelectedAnalysisDate] = useState('latest');
  const [allSleepSessions, setAllSleepSessions] = useState([]);
  
  // Fitbit connection state
  const [fitbitConnected, setFitbitConnected] = useState(false);
  const [fitbitConnection, setFitbitConnection] = useState(null);
  const [fitbitLoading, setFitbitLoading] = useState(false);
  // Garmin connection state
  const [garminConnected, setGarminConnected] = useState(false);
  const [garminConnection, setGarminConnection] = useState(null);
  const [garminLoading, setGarminLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [showConnectionModal, setShowConnectionModal] = useState(false);
  const [showWearablesDropdown, setShowWearablesDropdown] = useState(false);
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [pendingAction, setPendingAction] = useState(null); // 'manualEntry' or 'connect'
  const [activeDataTab, setActiveDataTab] = useState('sleep'); // 'sleep', 'heart', 'body'
  const [showGarminSyncResultModal, setShowGarminSyncResultModal] = useState(false);
  const [garminSyncResult, setGarminSyncResult] = useState(null); // { success, data, error }

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showWearablesDropdown && !event.target.closest('.wearables-dropdown-container')) {
        setShowWearablesDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showWearablesDropdown]);

  // Get userId from auth if not provided as prop
  useEffect(() => {
    const getUserId = async () => {
      if (userIdProp) {
        setUserId(userIdProp);
        return;
      }
      
      try {
        const { data: { user }, error: authError } = await supabase.auth.getUser();
        if (authError) {
          return;
        }
        if (user) {
          setUserId(user.id);
        }
      } catch (error) {
        // Error getting user ID
      }
    };
    
    getUserId();
  }, [userIdProp]);

  // Sleep data from database
  const [sleepData, setSleepData] = useState({
    lastNight: {
      totalSleep: 0,
      deepSleep: 0,
      remSleep: 0,
      lightSleep: 0,
      awakeTime: 0,
      sleepScore: null,
      sleepEfficiency: null,
      bedTime: '--:--',
      wakeTime: '--:--'
    },
    weekly: [
      { date: 'Mon', total: 0, deep: 0, rem: 0, light: 0 },
      { date: 'Tue', total: 0, deep: 0, rem: 0, light: 0 },
      { date: 'Wed', total: 0, deep: 0, rem: 0, light: 0 },
      { date: 'Thu', total: 0, deep: 0, rem: 0, light: 0 },
      { date: 'Fri', total: 0, deep: 0, rem: 0, light: 0 },
      { date: 'Sat', total: 0, deep: 0, rem: 0, light: 0 },
      { date: 'Sun', total: 0, deep: 0, rem: 0, light: 0 }
    ]
  });

  // Fetch sleep data from database
  const fetchSleepData = async () => {
    if (!userId) {
      setLoading(false);
      return;
    }

    try {
      // First, get the absolute most recent session for the card (no date filter)
      const latestSessionsResult = await dedupeRequest(
        getCacheKey('fetchSleepData_latestSession', userId),
        async () => {
          const { data, error } = await supabase
            .from('sleep_sessions')
            .select('*')
            .eq('user_id', userId)
            .order('session_start', { ascending: false })
            .limit(1);
          
          if (error) throw error;
          return { data, error };
        }
      );
      
      const { data: latestSessionsData, error: latestError } = latestSessionsResult;
      
      if (latestError) {
        throw latestError;
      }
      
      // Get the first (and only) session from the array, or null if no sessions
      const absoluteLatestSession = latestSessionsData && latestSessionsData.length > 0 ? latestSessionsData[0] : null;
      
      // Get the most recent session date to use as anchor point for weekly data
      const latestSessionDate = absoluteLatestSession?.session_start;
      
      let sessions = [];
      let sessionsError = null;
      
      if (latestSessionDate) {
        // Use the most recent session's date as anchor
        const latestDate = new Date(latestSessionDate);
        const sevenDaysBefore = new Date(latestDate);
        sevenDaysBefore.setDate(sevenDaysBefore.getDate() - 7);
        
        // Fetch all sessions within 7 days before the latest session
        const sessionsResult = await dedupeRequest(
          getCacheKey('fetchSleepData_weeklySessions', [userId, sevenDaysBefore.toISOString(), latestDate.toISOString()]),
          async () => {
            const { data, error } = await supabase
              .from('sleep_sessions')
              .select('*')
              .eq('user_id', userId)
              .gte('session_start', sevenDaysBefore.toISOString())
              .lte('session_start', latestDate.toISOString())
              .order('session_start', { ascending: false });
            
            return { data, error };
          }
        );

        sessions = sessionsResult.data || [];
        sessionsError = sessionsResult.error;
      } else {
        sessions = [];
      }

      if (sessionsError) {
        throw sessionsError;
      }

      // Store sessions for detailed chart
      setSleepSessions(sessions || []);
      
      // Store all sessions for date selection
      // Fetch all sessions to populate the dropdown
      const allSessionsResult = await dedupeRequest(
        getCacheKey('fetchSleepData_allSessions', userId),
        async () => {
          const { data, error } = await supabase
            .from('sleep_sessions')
            .select('*')
            .eq('user_id', userId)
            .order('session_start', { ascending: false });
          
          return { data, error };
        }
      );
      
      const { data: allSessionsData, error: allSessionsError } = allSessionsResult;
      
      if (allSessionsError) {
        // Error fetching all sessions
      } else {
        setAllSleepSessions(allSessionsData || []);
      }

      // Use the absolute latest session for "last night" card (not filtered by 7-day window)
      const lastSession = absoluteLatestSession;
      
      // Fetch sleep stages for the last session (using correct column name 'stage')
      let lastNightStages = null;
      if (lastSession) {
        const stagesResult = await dedupeRequest(
          getCacheKey('fetchSleepData_rawSleepStages', [userId, lastSession.id]),
          async () => {
            const { data, error } = await supabase
              .from('raw_sleep_stages')
              .select('id, session_id, stage, start_time, end_time, confidence')
              .eq('session_id', lastSession.id)
              .eq('user_id', userId)
              .order('start_time', { ascending: true });
            
            if (error) throw error;
            return { data, error };
          }
        );

        const { data: stages, error: stagesError } = stagesResult;
        
        if (stagesError) {
          throw stagesError;
        }
        lastNightStages = stages;
      }

      // Transform sessions data for weekly chart
      const weeklyData = transformSessionsToWeekly(sessions);
      
      // Transform last night data
      const lastNightData = transformLastNightData(lastSession, lastNightStages);

      // Update sleep data
      setSleepData({
        lastNight: lastNightData,
        weekly: weeklyData
      });
    } catch (error) {
      // Error fetching sleep data
    } finally {
      setLoading(false);
    }
  };

  // Transform sleep sessions to weekly format
  const transformSessionsToWeekly = (sessions) => {
    if (!sessions || sessions.length === 0) {
      // Return empty data structure if no sessions
      const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
      return days.map(date => ({ date, total: 0, deep: 0, rem: 0, light: 0 }));
    }

    // Group sessions by day of week
    const dayMap = {};
    
    sessions.forEach(session => {
      const sessionDate = new Date(session.session_start);
      const dayOfWeek = sessionDate.getDay(); // 0 = Sunday, 1 = Monday, etc.
      const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      const dayName = dayNames[dayOfWeek];
      
      if (!dayMap[dayName]) {
        dayMap[dayName] = {
          date: dayName,
          total: 0,
          deep: 0,
          rem: 0,
          light: 0,
          count: 0
        };
      }
      
      // Convert minutes to hours
      // Total sleep = actual sleep time (excluding awake)
      const deep = (session.deep_sleep_minutes || 0) / 60;
      const rem = (session.rem_sleep_minutes || 0) / 60;
      const light = (session.light_sleep_minutes || 0) / 60;
      dayMap[dayName].total += deep + rem + light;
      dayMap[dayName].deep += deep;
      dayMap[dayName].rem += rem;
      dayMap[dayName].light += light;
      dayMap[dayName].count += 1;
    });

    // Average if multiple sessions per day
    Object.keys(dayMap).forEach(day => {
      if (dayMap[day].count > 1) {
        dayMap[day].total /= dayMap[day].count;
        dayMap[day].deep /= dayMap[day].count;
        dayMap[day].rem /= dayMap[day].count;
        dayMap[day].light /= dayMap[day].count;
      }
    });

    // Return in order: Mon-Sun
    const dayOrder = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    return dayOrder.map(day => {
      const dayData = dayMap[day] || { date: day, total: 0, deep: 0, rem: 0, light: 0 };
      return {
        date: dayData.date,
        total: parseFloat((dayData.total || 0).toFixed(1)) || 0,
        deep: parseFloat((dayData.deep || 0).toFixed(1)) || 0,
        rem: parseFloat((dayData.rem || 0).toFixed(1)) || 0,
        light: parseFloat((dayData.light || 0).toFixed(1)) || 0
      };
    });
  };

  // Transform last night session data
  const transformLastNightData = (session, stages) => {
    if (!session) {
      return {
        totalSleep: 0,
        deepSleep: 0,
        remSleep: 0,
        lightSleep: 0,
        awakeTime: 0,
        sleepScore: null,
        sleepEfficiency: null,
        bedTime: '--:--',
        wakeTime: '--:--'
      };
    }

    // Convert minutes to hours
    const deepSleep = (session.deep_sleep_minutes || 0) / 60;
    const remSleep = (session.rem_sleep_minutes || 0) / 60;
    const lightSleep = (session.light_sleep_minutes || 0) / 60;
    const awakeTime = (session.awake_minutes || 0) / 60;
    // Total Sleep = actual sleep time (excluding awake time)
    const totalSleep = deepSleep + remSleep + lightSleep;

    // Format times
    const bedTime = session.session_start 
      ? new Date(session.session_start).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })
      : '--:--';
    const wakeTime = session.session_end
      ? new Date(session.session_end).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })
      : '--:--';

    const result = {
      totalSleep: parseFloat((totalSleep || 0).toFixed(1)) || 0,
      deepSleep: parseFloat((deepSleep || 0).toFixed(1)) || 0,
      remSleep: parseFloat((remSleep || 0).toFixed(1)) || 0,
      lightSleep: parseFloat((lightSleep || 0).toFixed(1)) || 0,
      awakeTime: parseFloat((awakeTime || 0).toFixed(1)) || 0,
      sleepScore: session.sleep_score || null,
      sleepEfficiency: session.sleep_efficiency ? parseFloat((session.sleep_efficiency * 100).toFixed(1)) : null,
      bedTime,
      wakeTime
    };

    return result;
  };

  useEffect(() => {
    if (userId) {
      fetchSleepData();
      checkFitbitConnection();
      checkGarminConnection();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // Handle URL parameters for wearable connection status
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const fitbitConnectedParam = searchParams.get('fitbit_connected');
    const fitbitErrorParam = searchParams.get('fitbit_error');
    const garminConnectedParam = searchParams.get('garmin_connected');
    const garminErrorParam = searchParams.get('garmin_error');

    if (fitbitConnectedParam === 'true') {
      alert('Fitbit connected successfully!');
      checkFitbitConnection();
      searchParams.delete('fitbit_connected');
      navigate({ search: searchParams.toString() }, { replace: true });
    } else if (fitbitErrorParam) {
      alert('Fitbit connection failed: ' + decodeURIComponent(fitbitErrorParam));
      searchParams.delete('fitbit_error');
      navigate({ search: searchParams.toString() }, { replace: true });
    } else if (garminConnectedParam === 'true') {
      alert('Garmin connected successfully!');
      checkGarminConnection();
      searchParams.delete('garmin_connected');
      navigate({ search: searchParams.toString() }, { replace: true });
    } else if (garminErrorParam) {
      alert('Garmin connection failed: ' + decodeURIComponent(garminErrorParam));
      searchParams.delete('garmin_error');
      navigate({ search: searchParams.toString() }, { replace: true });
    }
  }, [location.search, navigate]);

  // Check Fitbit connection status
  const checkFitbitConnection = async () => {
    if (!userId) return;
    
    setFitbitLoading(true);
    try {
      const result = await dedupeRequest(
        getCacheKey('checkFitbitConnection', userId),
        () => wearableService.getFitbitConnection(userId)
      );
      if (result.success) {
        setFitbitConnected(result.connected);
        setFitbitConnection(result.connection);
      }
    } catch (error) {
      // Error checking Fitbit connection
    } finally {
      setFitbitLoading(false);
    }
  };

  // Handle Fitbit connect
  const handleConnectFitbit = async () => {
    if (!userId) return;
    
    setFitbitLoading(true);
    try {
      await wearableService.connectFitbit(userId);
      // Will redirect, so no need to update state or close modal
    } catch (error) {
      alert(getUserFacingErrorMessage(error, 'Failed to connect Fitbit. Please try again.'));
      setFitbitLoading(false);
    }
  };

  // Close modal on Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && showConnectionModal) {
        setShowConnectionModal(false);
      }
    };

    if (showConnectionModal) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [showConnectionModal]);

  // Handle Fitbit disconnect
  const handleDisconnectFitbit = async () => {
    if (!userId) return;
    
    if (!window.confirm('Are you sure you want to disconnect Fitbit? Your data will no longer sync automatically.')) {
      return;
    }
    
    setFitbitLoading(true);
    try {
      const result = await wearableService.disconnectFitbit(userId);
      if (result.success) {
        setFitbitConnected(false);
        setFitbitConnection(null);
        alert('Fitbit disconnected successfully');
      } else {
        alert(getUserFacingErrorMessage(null, 'Failed to disconnect Fitbit. Please try again.'));
      }
    } catch (error) {
      alert(getUserFacingErrorMessage(error, 'Failed to disconnect Fitbit. Please try again.'));
    } finally {
      setFitbitLoading(false);
    }
  };

  // Handle manual sync
  const handleSyncFitbit = async () => {
    if (!userId) return;
    
    setSyncing(true);
    try {
      const result = await wearableService.syncFitbitData(userId);
      if (result.success) {
        alert('Data synced successfully!');
        await fetchSleepData();
        await checkFitbitConnection();
      } else {
        alert(getUserFacingErrorMessage(null, 'Failed to sync data. Please try again.'));
      }
    } catch (error) {
      alert(getUserFacingErrorMessage(error, 'Failed to sync data. Please try again.'));
    } finally {
      setSyncing(false);
    }
  };

  // Check Garmin connection status
  const checkGarminConnection = async () => {
    if (!userId) return;
    setGarminLoading(true);
    try {
      const result = await dedupeRequest(
        getCacheKey('checkGarminConnection', userId),
        () => wearableService.getGarminConnection(userId)
      );
      if (result.success) {
        setGarminConnected(result.connected);
        setGarminConnection(result.connection);
      }
    } catch (error) {
      // Error checking Garmin connection
    } finally {
      setGarminLoading(false);
    }
  };

  // Handle Garmin connect
  const handleConnectGarmin = async () => {
    if (!userId) return;
    setGarminLoading(true);
    try {
      await wearableService.connectGarmin(userId);
    } catch (error) {
      alert(getUserFacingErrorMessage(error, 'Failed to connect Garmin. Please try again.'));
      setGarminLoading(false);
    }
  };

  // Handle Garmin disconnect
  const handleDisconnectGarmin = async () => {
    if (!userId) return;
    if (!window.confirm('Are you sure you want to disconnect Garmin? Your data will no longer sync automatically.')) {
      return;
    }
    setGarminLoading(true);
    try {
      const result = await wearableService.disconnectGarmin(userId);
      if (result.success) {
        setGarminConnected(false);
        setGarminConnection(null);
        alert('Garmin disconnected successfully');
      } else {
        alert(getUserFacingErrorMessage(null, 'Failed to disconnect Garmin. Please try again.'));
      }
    } catch (error) {
      alert(getUserFacingErrorMessage(error, 'Failed to disconnect Garmin. Please try again.'));
    } finally {
      setGarminLoading(false);
    }
  };

  // Handle Garmin sync: pulls Activity API data (activities, moveIQ, etc.) and shows result modal.
  const handleSyncGarmin = async () => {
    if (!userId) return;
    setSyncing(true);
    setGarminSyncResult(null);
    try {
      const result = await wearableService.syncGarminData(userId);
      await checkGarminConnection();
      setGarminSyncResult({
        success: result.success,
        data: result.data,
        error: result.error
      });
      setShowGarminSyncResultModal(true);
    } catch (error) {
      setGarminSyncResult({ success: false, error: getUserFacingErrorMessage(error, 'Sync failed. Please try again.') });
      setShowGarminSyncResultModal(true);
    } finally {
      setSyncing(false);
    }
  };

  // Check if user has health data consent
  const checkHealthDataConsent = async (userId) => {
    if (!userId) return false;
    try {
      const { data, error } = await supabase
        .from('health_data_consent')
        .select('id')
        .eq('user_id', userId)
        .eq('consent_type', 'health_biometric_processing')
        .eq('is_active', true)
        .maybeSingle();

      if (error) {
        console.error('Error checking health data consent:', error);
        return false;
      }

      return !!data;
    } catch (error) {
      console.error('Error checking health data consent:', error);
      return false;
    }
  };

  // Store health data consent
  const storeHealthDataConsent = async (userId) => {
    if (!userId) return false;
    try {
      const consentText = "I explicitly consent to the processing of my health and biometric data, including wearable data and laboratory biomarkers (e.g. hormone levels), to provide personalised health insights and reports.";
      const consentTextVersion = "v1.0-2026-01-12";

      const { error } = await supabase
        .from('health_data_consent')
        .insert({
          user_id: userId,
          consent_type: 'health_biometric_processing',
          consent_text_version: consentTextVersion,
          consent_text_snapshot: consentText,
          given_at: new Date().toISOString()
        });

      if (error) {
        console.error('Error storing health data consent:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error storing health data consent:', error);
      return false;
    }
  };

  // Handle Manual Entry button click - check consent first
  const handleManualEntryClick = async () => {
    if (!userId) {
      setShowInputModal(true);
      return;
    }

    try {
      const userHasConsented = await checkHealthDataConsent(userId);
      
      if (!userHasConsented) {
        setPendingAction('manualEntry');
        setShowConsentModal(true);
        return;
      }

      // User has consented, proceed with opening input modal
      setShowInputModal(true);
    } catch (error) {
      console.error('Error checking consent:', error);
      // On error, allow the user to proceed (fail open)
      setShowInputModal(true);
    }
  };

  // Handle Connect button click - check consent first
  const handleConnectClick = async () => {
    if (!userId) {
      setShowConnectionModal(true);
      return;
    }

    try {
      const userHasConsented = await checkHealthDataConsent(userId);
      
      if (!userHasConsented) {
        setPendingAction('connect');
        setShowConsentModal(true);
        return;
      }

      // User has consented, proceed with opening connection modal
      setShowConnectionModal(true);
    } catch (error) {
      console.error('Error checking consent:', error);
      // On error, allow the user to proceed (fail open)
      setShowConnectionModal(true);
    }
  };

  // Handle consent modal actions
  const handleConsent = async () => {
    try {
      if (!userId) {
        setShowConsentModal(false);
        return;
      }

      // Store consent in database
      const consentStored = await storeHealthDataConsent(userId);
      
      if (consentStored) {
        setShowConsentModal(false);
        
        // Execute the pending action after consent is given
        if (pendingAction === 'manualEntry') {
          setShowInputModal(true);
        } else if (pendingAction === 'connect') {
          setShowConnectionModal(true);
        }
        
        setPendingAction(null);
      } else {
        alert('Error storing consent. Please try again.');
      }
    } catch (error) {
      console.error('Error handling consent:', error);
      alert('Error storing consent. Please try again.');
    }
  };

  const handleNoConsent = () => {
    // Just close the modal, nothing changes
    setShowConsentModal(false);
    setPendingAction(null);
  };

  // Initialize input data
  useEffect(() => {
    if (showInputModal) {
      const today = new Date().toISOString().split('T')[0];
      setInputData({
        date: today,
        totalSleep: '',
        deepSleep: '',
        remSleep: '',
        lightSleep: '',
        awakeTime: '',
        bedTime: '',
        wakeTime: '',
        sleepScore: '',
        sleepEfficiency: ''
      });
      setActiveDataTab('sleep');
    }
  }, [showInputModal]);

  // Initialize sleep detail date range when modal opens
  useEffect(() => {
    if (showSleepDetailModal) {
      // Set to today and today-7
      const today = new Date();
      const sevenDaysAgo = new Date(today);
      sevenDaysAgo.setDate(today.getDate() - 7);
      
      setSleepDetailDateRange({
        startDate: sevenDaysAgo.toISOString().split('T')[0],
        endDate: today.toISOString().split('T')[0]
      });
      setSleepDetailShowPercentage(false);
    }
  }, [showSleepDetailModal]);

  const handleInputChange = (field, value) => {
    setInputData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      if (!userId) {
        throw new Error('User ID is required');
      }

      // Parse the date and times
      const dateStr = inputData.date;
      const bedTimeStr = inputData.bedTime || '22:00';
      const wakeTimeStr = inputData.wakeTime || '06:00';
      
      // Create session_start and session_end timestamps
      const sessionStart = new Date(`${dateStr}T${bedTimeStr}:00`);
      const sessionEnd = new Date(`${dateStr}T${wakeTimeStr}:00`);
      
      // If wake time is earlier than bed time, assume it's the next day
      if (sessionEnd <= sessionStart) {
        sessionEnd.setDate(sessionEnd.getDate() + 1);
      }
      
      // Calculate duration in minutes
      const durationMinutes = Math.round((sessionEnd - sessionStart) / (1000 * 60));
      
      // Convert sleep times from hours to minutes
      const totalSleepMinutes = Math.round((parseFloat(inputData.totalSleep) || 0) * 60);
      const deepSleepMinutes = Math.round((parseFloat(inputData.deepSleep) || 0) * 60);
      const remSleepMinutes = Math.round((parseFloat(inputData.remSleep) || 0) * 60);
      const lightSleepMinutes = Math.round((parseFloat(inputData.lightSleep) || 0) * 60);
      const awakeMinutes = Math.round((parseFloat(inputData.awakeTime) || 0) * 60);
      
      // Get sleep efficiency from input (0-1) or calculate from total sleep / duration
      let sleepEfficiency = null;
      if (inputData.sleepEfficiency) {
        // User provided efficiency (0-1), convert to decimal
        sleepEfficiency = parseFloat(inputData.sleepEfficiency);
      } else if (totalSleepMinutes > 0) {
        // Calculate from total sleep / duration (as decimal 0-1)
        sleepEfficiency = totalSleepMinutes / durationMinutes;
      }
      
      // Get sleep score from input (0-100)
      const sleepScore = inputData.sleepScore ? parseInt(inputData.sleepScore) : null;
      
      // Create sleep session record
      const { data: session, error: sessionError } = await supabase
        .from('sleep_sessions')
        .insert({
          user_id: userId,
          source: 'manual',
          session_start: sessionStart.toISOString(),
          session_end: sessionEnd.toISOString(),
          duration_minutes: durationMinutes,
          sleep_efficiency: sleepEfficiency,
          total_sleep_minutes: totalSleepMinutes,
          deep_sleep_minutes: deepSleepMinutes,
          rem_sleep_minutes: remSleepMinutes,
          light_sleep_minutes: lightSleepMinutes,
          awake_minutes: awakeMinutes,
          sleep_score: sleepScore,
          is_nap: false,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
        })
        .select()
        .single();
      
      if (sessionError) throw sessionError;
      
      // Refresh the dashboard data
      await fetchSleepData();
      
      // Close modal and reset
      setShowInputModal(false);
      setInputData({});
    } catch (error) {
      alert(getUserFacingErrorMessage(error, 'Failed to save data. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  // Get stacked area chart option for sleep breakdown
  const getSleepDetailChartOption = () => {
    // If no sessions, return empty chart
    if (!sleepSessions || sleepSessions.length === 0) {
      return {
        tooltip: { trigger: 'axis' },
        xAxis: { type: 'category', data: [] },
        yAxis: { type: 'value' },
        series: []
      };
    }

    // Filter sessions by date range if date range is set
    let filteredSessions = sleepSessions;
    
    if (sleepDetailDateRange.startDate && sleepDetailDateRange.endDate) {
      const startDate = new Date(sleepDetailDateRange.startDate);
      startDate.setHours(0, 0, 0, 0);
      const endDate = new Date(sleepDetailDateRange.endDate);
      endDate.setHours(23, 59, 59, 999); // Include the entire end date

      filteredSessions = sleepSessions.filter(session => {
        if (!session.session_start) return false;
        const sessionDate = new Date(session.session_start);
        sessionDate.setHours(0, 0, 0, 0);
        return sessionDate >= startDate && sessionDate <= endDate;
      });
    }

    // If no sessions match the date range, show all sessions
    if (filteredSessions.length === 0 && sleepSessions.length > 0) {
      filteredSessions = sleepSessions;
    }

    // Get all sessions with their breakdown data
    const sessionsWithBreakdown = filteredSessions.map(session => {
      const date = new Date(session.session_start);
      const dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      
      const deep = (session.deep_sleep_minutes || 0) / 60;
      const rem = (session.rem_sleep_minutes || 0) / 60;
      const light = (session.light_sleep_minutes || 0) / 60;
      const awake = (session.awake_minutes || 0) / 60;
      const total = deep + rem + light + awake;

      return {
        date: dateStr,
        dateObj: date, // Keep original date for sorting
        deep,
        rem,
        light,
        awake,
        total
      };
    });

    // Sort by date (using dateObj)
    sessionsWithBreakdown.sort((a, b) => {
      return a.dateObj - b.dateObj;
    });

    const dates = sessionsWithBreakdown.map(d => d.date);
    
    // If no data after processing, return empty chart
    if (dates.length === 0) {
      return {
        tooltip: { trigger: 'axis' },
        xAxis: { type: 'category', data: [] },
        yAxis: { type: 'value' },
        series: []
      };
    }
    
    // Calculate data based on toggle (hours or percentages)
    let deepData, remData, lightData, awakeData;
    let yAxisName, yAxisFormatter, tooltipFormatter;

    if (sleepDetailShowPercentage) {
      // Calculate percentages - percentage of total time (all stages including awake)
      deepData = sessionsWithBreakdown.map(d => {
        const percentage = d.total > 0 ? (d.deep / d.total) * 100 : 0;
        return parseFloat(percentage.toFixed(1));
      });
      remData = sessionsWithBreakdown.map(d => {
        const percentage = d.total > 0 ? (d.rem / d.total) * 100 : 0;
        return parseFloat(percentage.toFixed(1));
      });
      lightData = sessionsWithBreakdown.map(d => {
        const percentage = d.total > 0 ? (d.light / d.total) * 100 : 0;
        return parseFloat(percentage.toFixed(1));
      });
      awakeData = sessionsWithBreakdown.map(d => {
        const percentage = d.total > 0 ? (d.awake / d.total) * 100 : 0;
        return parseFloat(percentage.toFixed(1));
      });
      
      yAxisName = 'Percentage';
      yAxisFormatter = '{value}%';
      tooltipFormatter = function(params) {
        let result = params[0].name + '<br/>';
        params.forEach(param => {
          result += param.marker + param.seriesName + ': ' + param.value + '%<br/>';
        });
        return result;
      };
    } else {
      // Use hours
      deepData = sessionsWithBreakdown.map(d => parseFloat(d.deep.toFixed(1)));
      remData = sessionsWithBreakdown.map(d => parseFloat(d.rem.toFixed(1)));
      lightData = sessionsWithBreakdown.map(d => parseFloat(d.light.toFixed(1)));
      awakeData = sessionsWithBreakdown.map(d => parseFloat(d.awake.toFixed(1)));
      
      yAxisName = 'Hours';
      yAxisFormatter = '{value}h';
      tooltipFormatter = function(params) {
        let result = params[0].name + '<br/>';
        let total = 0;
        params.forEach(param => {
          result += param.marker + param.seriesName + ': ' + param.value + 'h<br/>';
          total += param.value;
        });
        result += '<b>Total: ' + total.toFixed(1) + 'h</b>';
        return result;
      };
    }

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'cross'
        },
        formatter: tooltipFormatter
      },
      legend: {
        data: ['Deep', 'REM', 'Light', 'Awake'],
        bottom: 10,
        textStyle: {
          color: '#94a3b8'
        }
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '15%',
        top: '10%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: dates,
        axisLabel: {
          color: '#94a3b8'
        },
        axisLine: {
          lineStyle: {
            color: '#e5e7eb'
          }
        }
      },
      yAxis: {
        type: 'value',
        name: yAxisName,
        nameTextStyle: {
          color: '#94a3b8'
        },
        axisLabel: {
          color: '#94a3b8',
          formatter: yAxisFormatter
        },
        max: sleepDetailShowPercentage ? 100 : undefined,
        axisLine: {
          lineStyle: {
            color: '#e5e7eb'
          }
        },
        splitLine: {
          lineStyle: {
            color: '#e5e7eb',
            type: 'dashed'
          }
        }
      },
      series: [
        {
          name: 'Deep',
          type: 'line',
          stack: 'Total',
          areaStyle: {},
          data: deepData,
          itemStyle: {
            color: '#6366f1'
          },
          emphasis: {
            focus: 'series'
          }
        },
        {
          name: 'REM',
          type: 'line',
          stack: 'Total',
          areaStyle: {},
          data: remData,
          itemStyle: {
            color: '#8b5cf6'
          },
          emphasis: {
            focus: 'series'
          }
        },
        {
          name: 'Light',
          type: 'line',
          stack: 'Total',
          areaStyle: {},
          data: lightData,
          itemStyle: {
            color: '#ec4899'
          },
          emphasis: {
            focus: 'series'
          }
        },
        {
          name: 'Awake',
          type: 'line',
          stack: 'Total',
          areaStyle: {},
          data: awakeData,
          itemStyle: {
            color: '#f59e0b'
          },
          emphasis: {
            focus: 'series'
          }
        }
      ]
    };
  };

  // Chart configurations
  const getSleepChartOption = () => {
    const data = sleepData.weekly;
    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'cross' }
      },
      legend: {
        data: ['Total Sleep', 'Deep Sleep', 'REM Sleep', 'Light Sleep']
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: data.map(d => d.date)
      },
      yAxis: {
        type: 'value',
        name: 'Hours'
      },
      series: [
        {
          name: 'Total Sleep',
          type: 'bar',
          data: data.map(d => d.total),
          itemStyle: { color: '#288B80' }
        },
        {
          name: 'Deep Sleep',
          type: 'line',
          data: data.map(d => d.deep),
          itemStyle: { color: '#1e40af' }
        },
        {
          name: 'REM Sleep',
          type: 'line',
          data: data.map(d => d.rem),
          itemStyle: { color: '#ec4899' }
        },
        {
          name: 'Light Sleep',
          type: 'line',
          data: data.map(d => d.light),
          itemStyle: { color: '#60a5fa' }
        }
      ]
    };
  };

  const getSleepPieOption = () => {
    const sleep = sleepData.lastNight;
    return {
      tooltip: {
        trigger: 'item',
        formatter: '{b}: {c} hours ({d}%)'
      },
      series: [{
        name: 'Sleep Stages',
        type: 'pie',
        radius: ['40%', '70%'],
        center: ['50%', '50%'],
        data: [
          { value: sleep.deepSleep, name: 'Deep', itemStyle: { color: '#1e40af' } },
          { value: sleep.remSleep, name: 'REM', itemStyle: { color: '#ec4899' } },
          { value: sleep.lightSleep, name: 'Light', itemStyle: { color: '#60a5fa' } },
          { value: sleep.awakeTime, name: 'Awake', itemStyle: { color: '#f59e0b' } }
        ],
        emphasis: {
          itemStyle: {
            shadowBlur: 10,
            shadowOffsetX: 0,
            shadowColor: 'rgba(0, 0, 0, 0.5)'
          }
        }
      }]
    };
  };

  // Get available analysis dates from sleep sessions
  const getAnalysisDateOptions = () => {
    const options = [
      { value: 'latest', label: 'Latest' }
    ];
    
    if (!allSleepSessions || allSleepSessions.length === 0) {
      return options;
    }
    
    // Get unique dates from sleep sessions, sorted by date (newest first)
    const uniqueDates = [...new Set(
      allSleepSessions
        .map(session => {
          if (!session.session_start) return null;
          const date = new Date(session.session_start);
          return date.toISOString().split('T')[0];
        })
        .filter(date => date !== null)
    )].sort((a, b) => new Date(b) - new Date(a));
    
    // Add date options
    uniqueDates.forEach(date => {
      const dateObj = new Date(date);
      const formattedDate = dateObj.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric', 
        year: 'numeric' 
      });
      options.push({
        value: date,
        label: formattedDate
      });
    });
    
    return options;
  };

  // Get the selected session based on selectedAnalysisDate
  const getSelectedSession = () => {
    if (!allSleepSessions || allSleepSessions.length === 0) {
      return null;
    }
    
    if (selectedAnalysisDate === 'latest') {
      // Return the most recent session
      return allSleepSessions[0] || null;
    }
    
    // Find session matching the selected date
    const selectedDate = new Date(selectedAnalysisDate);
    selectedDate.setHours(0, 0, 0, 0);
    
    const matchingSession = allSleepSessions.find(session => {
      if (!session.session_start) return false;
      const sessionDate = new Date(session.session_start);
      sessionDate.setHours(0, 0, 0, 0);
      return sessionDate.getTime() === selectedDate.getTime();
    });
    
    return matchingSession || null;
  };

  // Get displayed sleep data based on selected analysis date
  const getDisplayedSleepData = () => {
    const selectedSession = getSelectedSession();
    
    if (!selectedSession) {
      return {
        totalSleep: 0,
        deepSleep: 0,
        remSleep: 0,
        lightSleep: 0,
        awakeTime: 0,
        sleepScore: null,
        sleepEfficiency: null,
        bedTime: '--:--',
        wakeTime: '--:--'
      };
    }
    
    return transformLastNightData(selectedSession, null);
  };

  // Computed displayed sleep data
  const displayedSleepData = getDisplayedSleepData();

  // Get badge text based on selected analysis date
  const getBadgeText = () => {
    if (selectedAnalysisDate === 'latest') {
      return 'Sleep';
    }
    
    // Format the selected date
    const dateObj = new Date(selectedAnalysisDate);
    return dateObj.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  if (loading) {
    return (
      <div className="wearables-tab">
        <div className="wearables-loading">
          <JellyfishLoader />
        </div>
      </div>
    );
  }

  return (
    <div className="wearables-tab">
      <div className="wearables-header">
        <div className="wearables-header-left">
          <div className="wearables-action-group">
            {isOwnProfile && (
              <button 
                className="wearables-add-data-btn"
                onClick={handleManualEntryClick}
                title="Add sleep data manually"
              >
                <Icon name="Plus" />
                <span>Manual Entry</span>
              </button>
            )}
            <div className="wearables-analysis-selector">
              <select 
                value={selectedAnalysisDate}
                onChange={(e) => setSelectedAnalysisDate(e.target.value)}
                className="wearables-analysis-dropdown"
              >
                {getAnalysisDateOptions().map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            {isOwnProfile && (
              <>
                <div className="wearables-action-divider"></div>
                <button
                  className="wearables-connect-cta-btn"
                  onClick={handleConnectClick}
                  title="Connect wearable devices"
                >
                  <Icon name="Link" />
                  <span>Connect</span>
                </button>
              </>
            )}
          </div>
        </div>
        
        <div className="wearables-header-right">
          <div className="wearables-dropdown-container">
            <button 
              className="wearables-status-trigger"
              onClick={() => setShowWearablesDropdown(!showWearablesDropdown)}
            >
              <div className={`wearables-connection-status-dot ${fitbitConnected || garminConnected ? 'connected' : 'disconnected'}`}></div>
              <span className="wearables-status-name">
                {fitbitConnected && garminConnected ? 'Fitbit, Garmin' : fitbitConnected ? 'Fitbit' : garminConnected ? 'Garmin' : 'No Devices'}
              </span>
              <Icon name={showWearablesDropdown ? "ChevronUp" : "ChevronDown"} className="wearables-chevron" />
            </button>

            {showWearablesDropdown && (
              <div className="wearables-status-dropdown">
                {/* Fitbit Item */}
                <div className="wearables-dropdown-item">
                  <div className="wearables-dropdown-item-info">
                    <div className={`wearables-connection-status-dot ${fitbitConnected ? 'connected' : 'disconnected'}`}></div>
                    <span className="wearables-dropdown-name">Fitbit</span>
                  </div>
                  {fitbitConnected && isOwnProfile && (
                    <button
                      className="wearables-sync-btn-small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSyncFitbit();
                      }}
                      disabled={syncing}
                      title="Sync data"
                    >
                      <Icon name={syncing ? "Loader" : "RefreshCw"} />
                    </button>
                  )}
                </div>

                {/* Garmin Item */}
                <div className="wearables-dropdown-item">
                  <div className="wearables-dropdown-item-info">
                    <div className={`wearables-connection-status-dot ${garminConnected ? 'connected' : 'disconnected'}`}></div>
                    <span className="wearables-dropdown-name">Garmin</span>
                  </div>
                  {garminConnected && isOwnProfile && (
                    <button
                      className="wearables-sync-btn-small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSyncGarmin();
                      }}
                      disabled={syncing}
                      title="Sync data"
                    >
                      <Icon name={syncing ? "Loader" : "RefreshCw"} />
                    </button>
                  )}
                </div>
                
                <div className="wearables-dropdown-item">
                  <div className="wearables-dropdown-item-info">
                     <div className="wearables-connection-status-dot disconnected"></div>
                     <span className="wearables-dropdown-name">Whoop</span>
                  </div>
                </div>
                
                <div className="wearables-dropdown-item">
                  <div className="wearables-dropdown-item-info">
                     <div className="wearables-connection-status-dot disconnected"></div>
                     <span className="wearables-dropdown-name">Oura Ring</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Wearable Connection Modal */}
      {showConnectionModal && isOwnProfile && (
        <div 
          className="modal-overlay"
          onClick={(e) => {
            // Don't close on overlay click - user must explicitly choose
            e.stopPropagation();
          }}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <h3>Connect Wearable Devices</h3>
              <button
                className="wearables-connection-modal-close"
                onClick={() => setShowConnectionModal(false)}
              >
                <Icon name="X" />
              </button>
            </div>
            <div className="modal-body" style={{ padding: '24px' }}>
              {/* Connected Devices Section */}
              <div className="wearables-connection-section">
                <h4 className="wearables-connection-section-title">Connected</h4>
                {fitbitConnected && fitbitConnection ? (
                  <div className="wearables-connection-item connected">
                    <div className="wearables-connection-item-left">
                      <Icon name="Heart" className="wearables-connection-item-icon" />
                      <div className="wearables-connection-item-info">
                        <span className="wearables-connection-item-name">Fitbit</span>
                        {fitbitConnection.last_synced_at && (
                          <span className="wearables-connection-item-date">
                            Last synced: {new Date(fitbitConnection.last_synced_at).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="wearables-connection-item-actions">
                      <button
                        className="wearables-connection-disconnect-btn"
                        onClick={handleDisconnectFitbit}
                        disabled={fitbitLoading}
                      >
                        <Icon name="X" />
                        Disconnect
                      </button>
                    </div>
                  </div>
                ) : null}
                {garminConnected && garminConnection ? (
                  <div className="wearables-connection-item connected">
                    <div className="wearables-connection-item-left">
                      <Icon name="Heart" className="wearables-connection-item-icon" />
                      <div className="wearables-connection-item-info">
                        <span className="wearables-connection-item-name">Garmin</span>
                        {garminConnection.last_synced_at && (
                          <span className="wearables-connection-item-date">
                            Last synced: {new Date(garminConnection.last_synced_at).toLocaleDateString()}
                          </span>
                        )}
                        <span className="wearables-connection-item-hint" style={{ display: 'block', fontSize: '12px', color: 'var(--color-text-muted)', marginTop: '4px' }}>
                          New data appears after you sync your device in Garmin Connect
                        </span>
                      </div>
                    </div>
                    <div className="wearables-connection-item-actions">
                      <button
                        className="wearables-connection-disconnect-btn"
                        onClick={handleDisconnectGarmin}
                        disabled={garminLoading}
                      >
                        <Icon name="X" />
                        Disconnect
                      </button>
                    </div>
                  </div>
                ) : null}
                {!fitbitConnected && !garminConnected && (
                  <div className="wearables-connection-empty">
                    <p>No devices connected</p>
                  </div>
                )}
              </div>

              {/* Available Devices Section */}
              <div className="wearables-connection-section">
                <h4 className="wearables-connection-section-title">Available Devices</h4>
                <div className="wearables-connection-item available">
                  <div className="wearables-connection-item-left">
                    <Icon name="Heart" className="wearables-connection-item-icon" />
                    <div className="wearables-connection-item-info">
                      <span className="wearables-connection-item-name">Fitbit</span>
                    </div>
                  </div>
                  <div className="wearables-connection-item-actions">
                    <button
                      className="wearables-connection-connect-btn"
                      onClick={handleConnectFitbit}
                      disabled={fitbitLoading || fitbitConnected}
                    >
                      <Icon name="Link" />
                      {fitbitLoading ? 'Connecting...' : fitbitConnected ? 'Connected' : 'Connect'}
                    </button>
                  </div>
                </div>
                <div className="wearables-connection-item available">
                  <div className="wearables-connection-item-left">
                    <Icon name="Heart" className="wearables-connection-item-icon" />
                    <div className="wearables-connection-item-info">
                      <span className="wearables-connection-item-name">Garmin</span>
                      <span className="wearables-connection-item-hint" style={{ display: 'block', fontSize: '12px', color: 'var(--color-text-muted)', marginTop: '4px' }}>
                        Data syncs when you sync your device in Garmin Connect
                      </span>
                    </div>
                  </div>
                  <div className="wearables-connection-item-actions">
                    <button
                      className="wearables-connection-connect-btn"
                      onClick={handleConnectGarmin}
                      disabled={garminLoading || garminConnected}
                    >
                      <Icon name="Link" />
                      {garminLoading ? 'Connecting...' : garminConnected ? 'Connected' : 'Connect'}
                    </button>
                  </div>
                </div>
                <div className="wearables-connection-item available">
                  <div className="wearables-connection-item-left">
                    <Icon name="Heart" className="wearables-connection-item-icon" />
                    <div className="wearables-connection-item-info">
                      <span className="wearables-connection-item-name">Google Fit</span>
                    </div>
                  </div>
                  <div className="wearables-connection-item-actions">
                    <button
                      className="wearables-connection-connect-btn"
                      disabled={true}
                      title="Coming soon"
                    >
                      <Icon name="Link" />
                      Coming Soon
                    </button>
                  </div>
                </div>
                <div className="wearables-connection-item available">
                  <div className="wearables-connection-item-left">
                    <Icon name="Heart" className="wearables-connection-item-icon" />
                    <div className="wearables-connection-item-info">
                      <span className="wearables-connection-item-name">Whoop</span>
                    </div>
                  </div>
                  <div className="wearables-connection-item-actions">
                    <button
                      className="wearables-connection-connect-btn"
                      disabled={true}
                      title="Coming soon"
                    >
                      <Icon name="Link" />
                      Coming Soon
                    </button>
                  </div>
                </div>
                <div className="wearables-connection-item available">
                  <div className="wearables-connection-item-left">
                    <Icon name="Heart" className="wearables-connection-item-icon" />
                    <div className="wearables-connection-item-info">
                      <span className="wearables-connection-item-name">Oura Ring</span>
                    </div>
                  </div>
                  <div className="wearables-connection-item-actions">
                    <button
                      className="wearables-connection-connect-btn"
                      disabled={true}
                      title="Coming soon"
                    >
                      <Icon name="Link" />
                      Coming Soon
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="wearables-content">
        {/* Wearables Cards Grid */}
        <div className="wearables-cards-grid">
          {/* Sleep Card Preview */}
          <div className="sleep-card-preview">
            <div className="sleep-card">
              <div className="sleep-card-header">
                <div className="sleep-card-badge">
                  <Icon name="Moon" />
                  <span>{getBadgeText()}</span>
              </div>
                <button 
                  className="sleep-card-view-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowSleepDetailModal(true);
                  }}
                  title="View weekly sleep trends"                >
                  <Icon name="ArrowRight" />
                </button>
              </div>
              <div className="sleep-card-content">
                <div className="sleep-card-top-section">
                  <div className="sleep-card-main">
                    <div className="sleep-card-main-value">
                      {displayedSleepData.totalSleep > 0 
                        ? `${displayedSleepData.totalSleep}h`
                        : '--h'}
              </div>
                    <div className="sleep-card-main-label">Total Sleep</div>
                  </div>
                  <div className="sleep-card-top-right">
                    <div className="sleep-score-section-top">
                      <div className="sleep-score-icon-top">
                        <div className="sleep-score-value-inside">
                  {displayedSleepData.sleepScore !== null && displayedSleepData.sleepScore !== undefined 
                    ? displayedSleepData.sleepScore 
                    : '–'}
                </div>
              </div>
            </div>
            </div>
            </div>
                <div className="sleep-card-times">
                  <div className="sleep-time-value">{displayedSleepData.bedTime}</div>
                  <div className="sleep-time-connector">
                    <div className="sleep-time-line"></div>
            </div>
                  <div className="sleep-time-value">{displayedSleepData.wakeTime}</div>
                </div>
                <div className="sleep-card-bottom">
                  <div className="sleep-card-breakdown">
                    <div className="sleep-breakdown-item">
                      <div className="sleep-breakdown-label">Deep</div>
                      <div className="sleep-breakdown-value">{displayedSleepData.deepSleep}h</div>
                    </div>
                    <div className="sleep-breakdown-item">
                      <div className="sleep-breakdown-label">REM</div>
                      <div className="sleep-breakdown-value">{displayedSleepData.remSleep}h</div>
                    </div>
                    <div className="sleep-breakdown-item">
                      <div className="sleep-breakdown-label">Light</div>
                      <div className="sleep-breakdown-value">{displayedSleepData.lightSleep}h</div>
                    </div>
                    <div className="sleep-breakdown-item">
                      <div className="sleep-breakdown-label">Awake</div>
                      <div className="sleep-breakdown-value">{displayedSleepData.awakeTime}h</div>
                    </div>
                  </div>
                  <div className="sleep-efficiency-section" title="Efficiency">
                    <div className="sleep-efficiency-icon">
                      <Icon name="Target" />
                    </div>
                    <div className="sleep-efficiency-value">
                      {displayedSleepData.sleepEfficiency !== null && displayedSleepData.sleepEfficiency !== undefined 
                        ? `${displayedSleepData.sleepEfficiency}%`
                        : '–'}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Body Card Placeholder */}
          <div className="sleep-card-preview">
            <div className="sleep-card placeholder-card">
              <div className="sleep-card-header">
                <div className="sleep-card-badge">
                  <Icon name="Dumbbell" />
                  <span>Body</span>
                </div>
                <div className="placeholder-coming-soon">Coming Soon</div>
              </div>
              <div className="sleep-card-content">
                <div className="sleep-card-top-section">
                  <div className="sleep-card-main">
                    <div className="sleep-card-main-value">–</div>
                    <div className="sleep-card-main-label">Body Metrics</div>
                  </div>
                  <div className="sleep-card-top-right">
                    <div className="sleep-score-section-top">
                      <div className="sleep-score-icon-top">
                        <div className="sleep-score-value-inside">–</div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="sleep-card-times">
                  <div className="sleep-time-value">–</div>
                  <div className="sleep-time-connector">
                    <div className="sleep-time-line"></div>
                  </div>
                  <div className="sleep-time-value">–</div>
                </div>
                <div className="sleep-card-bottom">
                  <div className="sleep-card-breakdown">
                    <div className="sleep-breakdown-item">
                      <div className="placeholder-stat-box"></div>
                      <div className="sleep-breakdown-value">–</div>
                    </div>
                    <div className="sleep-breakdown-item">
                      <div className="placeholder-stat-box"></div>
                      <div className="sleep-breakdown-value">–</div>
                    </div>
                    <div className="sleep-breakdown-item">
                      <div className="placeholder-stat-box"></div>
                      <div className="sleep-breakdown-value">–</div>
                    </div>
                    <div className="sleep-breakdown-item">
                      <div className="placeholder-stat-box"></div>
                      <div className="sleep-breakdown-value">–</div>
                    </div>
                  </div>
                  <div className="sleep-efficiency-section">
                    <div className="sleep-efficiency-icon">
                      <Icon name="Target" />
                    </div>
                    <div className="sleep-efficiency-value">–</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Heart Card Placeholder */}
          <div className="sleep-card-preview">
            <div className="sleep-card placeholder-card">
              <div className="sleep-card-header">
                <div className="sleep-card-badge">
                  <Icon name="Heart" />
                  <span>Heart</span>
                </div>
                <div className="placeholder-coming-soon">Coming Soon</div>
              </div>
              <div className="sleep-card-content">
                <div className="sleep-card-top-section">
                  <div className="sleep-card-main">
                    <div className="sleep-card-main-value">–</div>
                    <div className="sleep-card-main-label">Heart Metrics</div>
                  </div>
                  <div className="sleep-card-top-right">
                    <div className="sleep-score-section-top">
                      <div className="sleep-score-icon-top">
                        <div className="sleep-score-value-inside">–</div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="sleep-card-times">
                  <div className="sleep-time-value">–</div>
                  <div className="sleep-time-connector">
                    <div className="sleep-time-line"></div>
                  </div>
                  <div className="sleep-time-value">–</div>
                </div>
                <div className="sleep-card-bottom">
                  <div className="sleep-card-breakdown">
                    <div className="sleep-breakdown-item">
                      <div className="placeholder-stat-box"></div>
                      <div className="sleep-breakdown-value">–</div>
                    </div>
                    <div className="sleep-breakdown-item">
                      <div className="placeholder-stat-box"></div>
                      <div className="sleep-breakdown-value">–</div>
                    </div>
                    <div className="sleep-breakdown-item">
                      <div className="placeholder-stat-box"></div>
                      <div className="sleep-breakdown-value">–</div>
                    </div>
                    <div className="sleep-breakdown-item">
                      <div className="placeholder-stat-box"></div>
                      <div className="sleep-breakdown-value">–</div>
                    </div>
                  </div>
                  <div className="sleep-efficiency-section">
                    <div className="sleep-efficiency-icon">
                      <Icon name="Target" />
                    </div>
                    <div className="sleep-efficiency-value">–</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Sleep Detail Chart Modal */}
      {showSleepDetailModal && (
        <div className="wearables-modal-overlay" onClick={() => setShowSleepDetailModal(false)}>
          <div className="wearables-modal wearables-modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="wearables-modal-header">
              <h3>Sleep Breakdown Over Time</h3>
              <button
                className="wearables-modal-close"
                onClick={() => setShowSleepDetailModal(false)}
                aria-label="Close modal"
              >
                <Icon name="X" />
              </button>
            </div>
            <div className="sleep-detail-controls">
              <div className="sleep-detail-date-range">
                <label htmlFor="sleep-detail-start-date">From:</label>
                <input
                  id="sleep-detail-start-date"
                  type="date"
                  value={sleepDetailDateRange.startDate}
                  onChange={(e) => setSleepDetailDateRange(prev => ({ ...prev, startDate: e.target.value }))}
                  className="sleep-detail-date-input"
                />
                <label htmlFor="sleep-detail-end-date">To:</label>
                <input
                  id="sleep-detail-end-date"
                  type="date"
                  value={sleepDetailDateRange.endDate}
                  onChange={(e) => setSleepDetailDateRange(prev => ({ ...prev, endDate: e.target.value }))}
                  className="sleep-detail-date-input"
                />
              </div>
              <div className="sleep-detail-toggle">
                <button
                  className={`sleep-detail-toggle-btn ${!sleepDetailShowPercentage ? 'active' : ''}`}
                  onClick={() => setSleepDetailShowPercentage(false)}
                >
                  Total
                </button>
                <button
                  className={`sleep-detail-toggle-btn ${sleepDetailShowPercentage ? 'active' : ''}`}
                  onClick={() => setSleepDetailShowPercentage(true)}
                >
                  %
                </button>
              </div>
            </div>
            <div className="wearables-modal-content">
              <ReactECharts 
                option={getSleepDetailChartOption()} 
                style={{ height: '500px', width: '100%' }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Data Input Modal */}
      {showInputModal && isOwnProfile && (
        <div className="modal-overlay" onClick={(e) => {
          // Don't close on overlay click - user must explicitly choose
          e.stopPropagation();
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <h3>Add Data</h3>
              <button 
                className="wearables-modal-close"
                onClick={() => setShowInputModal(false)}
              >
                <Icon name="X" />
              </button>
            </div>
            
            {/* Tabs */}
            <div className="wearables-data-tabs">
              <button
                className={`wearables-data-tab ${activeDataTab === 'sleep' ? 'active' : ''}`}
                onClick={() => setActiveDataTab('sleep')}
              >
                Sleep
              </button>
              <button
                className={`wearables-data-tab ${activeDataTab === 'heart' ? 'active' : ''}`}
                onClick={() => setActiveDataTab('heart')}
              >
                Heart
              </button>
              <button
                className={`wearables-data-tab ${activeDataTab === 'body' ? 'active' : ''}`}
                onClick={() => setActiveDataTab('body')}
              >
                Body
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="modal-body" style={{ padding: '24px' }}>
              {/* Sleep Tab */}
              {activeDataTab === 'sleep' && (
                <>
              {/* Date Input */}
              <div className="wearables-form-group">
                <label htmlFor="date">Date</label>
                <input
                  type="date"
                  id="date"
                  value={inputData.date || ''}
                  onChange={(e) => handleInputChange('date', e.target.value)}
                  required
                />
              </div>

              {/* Sleep Data Input */}
              <div className="wearables-form-row">
                <div className="wearables-form-group">
                  <label htmlFor="totalSleep">Total Sleep (hours)</label>
                  <input
                    type="number"
                    id="totalSleep"
                    step="0.1"
                    min="0"
                    max="24"
                    value={inputData.totalSleep || ''}
                    onChange={(e) => handleInputChange('totalSleep', e.target.value)}
                    placeholder="e.g., 7.5"
                  />
                </div>
                <div className="wearables-form-group">
                  <label htmlFor="deepSleep">Deep Sleep (hours)</label>
                  <input
                    type="number"
                    id="deepSleep"
                    step="0.1"
                    min="0"
                    max="24"
                    value={inputData.deepSleep || ''}
                    onChange={(e) => handleInputChange('deepSleep', e.target.value)}
                    placeholder="e.g., 1.8"
                  />
                </div>
              </div>

              <div className="wearables-form-row">
                <div className="wearables-form-group">
                  <label htmlFor="remSleep">REM Sleep (hours)</label>
                  <input
                    type="number"
                    id="remSleep"
                    step="0.1"
                    min="0"
                    max="24"
                    value={inputData.remSleep || ''}
                    onChange={(e) => handleInputChange('remSleep', e.target.value)}
                    placeholder="e.g., 1.5"
                  />
                </div>
                <div className="wearables-form-group">
                  <label htmlFor="lightSleep">Light Sleep (hours)</label>
                  <input
                    type="number"
                    id="lightSleep"
                    step="0.1"
                    min="0"
                    max="24"
                    value={inputData.lightSleep || ''}
                    onChange={(e) => handleInputChange('lightSleep', e.target.value)}
                    placeholder="e.g., 4.2"
                  />
                </div>
              </div>

              <div className="wearables-form-row">
                <div className="wearables-form-group">
                  <label htmlFor="awakeTime">Awake Time (hours)</label>
                  <input
                    type="number"
                    id="awakeTime"
                    step="0.1"
                    min="0"
                    max="24"
                    value={inputData.awakeTime || ''}
                    onChange={(e) => handleInputChange('awakeTime', e.target.value)}
                    placeholder="e.g., 0.3"
                  />
                </div>
                <div className="wearables-form-group">
                  <label htmlFor="sleepScore">Sleep Score (optional)</label>
                  <input
                    type="number"
                    id="sleepScore"
                    min="0"
                    max="100"
                    step="1"
                    value={inputData.sleepScore || ''}
                    onChange={(e) => handleInputChange('sleepScore', e.target.value)}
                    placeholder="e.g., 85"
                  />
                </div>
              </div>

              <div className="wearables-form-row">
                <div className="wearables-form-group">
                  <label htmlFor="sleepEfficiency">Sleep Efficiency (optional)</label>
                  <input
                    type="number"
                    id="sleepEfficiency"
                    min="0"
                    max="1"
                    step="0.01"
                    value={inputData.sleepEfficiency || ''}
                    onChange={(e) => handleInputChange('sleepEfficiency', e.target.value)}
                    placeholder="e.g., 0.85"
                  />
                </div>
              </div>

              <div className="wearables-form-row">
                <div className="wearables-form-group">
                  <label htmlFor="bedTime">Bedtime</label>
                  <input
                    type="time"
                    id="bedTime"
                    value={inputData.bedTime || ''}
                    onChange={(e) => handleInputChange('bedTime', e.target.value)}
                  />
                </div>
                <div className="wearables-form-group">
                  <label htmlFor="wakeTime">Wake Time</label>
                  <input
                    type="time"
                    id="wakeTime"
                    value={inputData.wakeTime || ''}
                    onChange={(e) => handleInputChange('wakeTime', e.target.value)}
                  />
                </div>
              </div>
                </>
              )}

              {/* Heart Tab */}
              {activeDataTab === 'heart' && (
                <>
                  <div className="wearables-form-group">
                    <label htmlFor="heartDate">Date</label>
                    <input
                      type="date"
                      id="heartDate"
                      value={inputData.date || ''}
                      onChange={(e) => handleInputChange('date', e.target.value)}
                    />
                  </div>

                  <div className="wearables-form-row">
                    <div className="wearables-form-group">
                      <label htmlFor="restingHeartRate">Resting Heart Rate (bpm)</label>
                      <input
                        type="number"
                        id="restingHeartRate"
                        step="1"
                        min="0"
                        max="200"
                        placeholder="e.g., 65"
                      />
                    </div>
                    <div className="wearables-form-group">
                      <label htmlFor="maxHeartRate">Max Heart Rate (bpm)</label>
                      <input
                        type="number"
                        id="maxHeartRate"
                        step="1"
                        min="0"
                        max="250"
                        placeholder="e.g., 180"
                      />
                    </div>
                  </div>

                  <div className="wearables-form-row">
                    <div className="wearables-form-group">
                      <label htmlFor="avgHeartRate">Average Heart Rate (bpm)</label>
                      <input
                        type="number"
                        id="avgHeartRate"
                        step="1"
                        min="0"
                        max="250"
                        placeholder="e.g., 72"
                      />
                    </div>
                    <div className="wearables-form-group">
                      <label htmlFor="hrv">HRV (ms)</label>
                      <input
                        type="number"
                        id="hrv"
                        step="1"
                        min="0"
                        placeholder="e.g., 45"
                      />
                    </div>
                  </div>

                  <div className="wearables-form-row">
                    <div className="wearables-form-group">
                      <label htmlFor="vo2Max">VO2 Max (optional)</label>
                      <input
                        type="number"
                        id="vo2Max"
                        step="0.1"
                        min="0"
                        placeholder="e.g., 52.5"
                      />
                    </div>
                  </div>
                </>
              )}

              {/* Body Tab */}
              {activeDataTab === 'body' && (
                <>
                  <div className="wearables-form-group">
                    <label htmlFor="bodyDate">Date</label>
                    <input
                      type="date"
                      id="bodyDate"
                      value={inputData.date || ''}
                      onChange={(e) => handleInputChange('date', e.target.value)}
                    />
                  </div>

                  <div className="wearables-form-row">
                    <div className="wearables-form-group">
                      <label htmlFor="weight">Weight (kg)</label>
                      <input
                        type="number"
                        id="weight"
                        step="0.1"
                        min="0"
                        placeholder="e.g., 75.5"
                      />
                    </div>
                    <div className="wearables-form-group">
                      <label htmlFor="bodyFat">Body Fat (%)</label>
                      <input
                        type="number"
                        id="bodyFat"
                        step="0.1"
                        min="0"
                        max="100"
                        placeholder="e.g., 15.2"
                      />
                    </div>
                  </div>

                  <div className="wearables-form-row">
                    <div className="wearables-form-group">
                      <label htmlFor="muscleMass">Muscle Mass (kg)</label>
                      <input
                        type="number"
                        id="muscleMass"
                        step="0.1"
                        min="0"
                        placeholder="e.g., 60.0"
                      />
                    </div>
                    <div className="wearables-form-group">
                      <label htmlFor="boneMass">Bone Mass (kg)</label>
                      <input
                        type="number"
                        id="boneMass"
                        step="0.1"
                        min="0"
                        placeholder="e.g., 3.5"
                      />
                    </div>
                  </div>

                  <div className="wearables-form-row">
                    <div className="wearables-form-group">
                      <label htmlFor="waterPercentage">Water % (optional)</label>
                      <input
                        type="number"
                        id="waterPercentage"
                        step="0.1"
                        min="0"
                        max="100"
                        placeholder="e.g., 60.0"
                      />
                    </div>
                  </div>
                </>
              )}

              <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
                <button 
                  type="button" 
                  className="btn btn--secondary btn--md btn--rounded"
                  onClick={() => setShowInputModal(false)}
                  style={{ flex: 1 }}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="btn btn--primary btn--md btn--rounded"
                  disabled={submitting || activeDataTab !== 'sleep'}
                  style={{ flex: 1 }}
                  title={activeDataTab !== 'sleep' ? 'Coming soon' : ''}
                >
                  {submitting ? 'Saving...' : activeDataTab === 'sleep' ? 'Save Data' : 'Coming Soon'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Garmin Sync Result Modal */}
      {showGarminSyncResultModal && (
        <div className="modal-overlay" onClick={(e) => e.stopPropagation()}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '520px' }}>
            <div className="modal-header">
              <h3>Garmin sync</h3>
              <button
                className="wearables-modal-close"
                onClick={() => setShowGarminSyncResultModal(false)}
                aria-label="Close"
              >
                <Icon name="X" />
              </button>
            </div>
            <div className="modal-body" style={{ padding: '24px' }}>
              {garminSyncResult?.success ? (
                <>
                  <p style={{ marginBottom: '16px', fontSize: '14px', lineHeight: '1.6', color: 'var(--color-text-primary)' }}>
                    <strong>What just happened</strong>
                  </p>
                  <ul style={{ marginBottom: '16px', paddingLeft: '20px', fontSize: '14px', lineHeight: '1.7', color: 'var(--color-text-primary)' }}>
                    <li>We refreshed your connection and pulled the latest <strong>activity data</strong> from Garmin for the last 30 days (workouts, runs, rides, Move IQ events, etc.).</li>
                    {garminSyncResult?.data?.pull && (
                      <>
                        {(garminSyncResult.data.pull.activities > 0 || garminSyncResult.data.pull.moveIQActivities > 0 || garminSyncResult.data.pull.manuallyUpdatedActivities > 0) && (
                          <li style={{ marginTop: '8px' }}>
                            This run: <strong>{garminSyncResult.data.pull.activities}</strong> activities, <strong>{garminSyncResult.data.pull.moveIQActivities}</strong> Move IQ events, <strong>{garminSyncResult.data.pull.manuallyUpdatedActivities}</strong> manually updated activities.
                          </li>
                        )}
                      </>
                    )}
                    <li style={{ marginTop: '8px' }}>Health data (dailies, sleep, epochs) is sent by Garmin when you sync in the <strong>Garmin Connect</strong> app or website. We request a one-time backfill when you first connect; after that, new health data arrives via push when you sync your device.</li>
                  </ul>
                  <p style={{ marginBottom: '12px', fontSize: '14px', lineHeight: '1.6', color: 'var(--color-text-primary)' }}>
                    <strong>To get new data</strong>
                  </p>
                  <ol style={{ marginBottom: '16px', paddingLeft: '20px', fontSize: '14px', lineHeight: '1.7', color: 'var(--color-text-primary)' }}>
                    <li>Open the <strong>Garmin Connect</strong> app (or connect.garmin.com) and sync your watch or device.</li>
                    <li>New data will then be sent to us automatically, or you can click Refresh here again to pull the latest activities.</li>
                  </ol>
                  {/* Health API debug: what we've received via push (and where historical data comes from) */}
                  {garminSyncResult?.data?.healthApiStatus && (
                    <details style={{ marginBottom: '16px', fontSize: '13px', color: 'var(--color-text-secondary)' }}>
                      <summary style={{ cursor: 'pointer', fontWeight: 600, marginBottom: '8px' }}>Debug: Health API (push data we have)</summary>
                      <div style={{ paddingLeft: '8px', borderLeft: '2px solid var(--color-border)', marginTop: '8px' }}>
                        <p style={{ margin: '4px 0' }}>{['dailies', 'epochs', 'sleeps'].map(t => {
                          const s = garminSyncResult.data.healthApiStatus[t];
                          if (!s) return `${t}: none yet`;
                          const range = s.dateFrom != null && s.dateTo != null ? ` ${s.dateFrom} → ${s.dateTo}` : '';
                          const rows = s.totalStoredRows != null ? ` (${s.totalStoredRows} stored)` : '';
                          const received = s.lastFetchedAt ? ` received ${new Date(s.lastFetchedAt).toLocaleString()}` : '';
                          return `${t}:${range}${rows}${received}`;
                        }).join(' · ')}</p>
                        <p style={{ margin: '8px 0 0', fontSize: '12px', opacity: 0.9 }}>
                          Historical data (e.g. 90 days) is requested once when you first connect; Garmin then POSTs it to us. If you only see one day or one “stored” chunk, that backfill may have been rate-limited. New data arrives when you sync in Garmin Connect.
                        </p>
                        <p style={{ margin: '4px 0 0', fontSize: '12px', opacity: 0.9 }}>Cloudflare logs: “[Garmin Push]”, “[Garmin Health API Push]”.</p>
                      </div>
                    </details>
                  )}
                  {(garminSyncResult?.data?.pull?.errors?.length > 0) && (
                    <p style={{ marginTop: '16px', fontSize: '12px', color: 'var(--color-text-muted)' }}>
                      Some requests had issues: {garminSyncResult.data.pull.errors.join('; ')}
                    </p>
                  )}
                </>
              ) : (
                <>
                  <p style={{ marginBottom: '16px', fontSize: '14px', lineHeight: '1.6', color: 'var(--color-text-primary)' }}>
                    Sync failed: {garminSyncResult?.error || 'Unknown error'}.
                  </p>
                  <p style={{ marginBottom: '0', fontSize: '14px', lineHeight: '1.6', color: 'var(--color-text-secondary)' }}>
                    Check that your Garmin connection is still active. If needed, disconnect and reconnect Garmin in the connection modal.
                  </p>
                </>
              )}
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '24px' }}>
                <button
                  type="button"
                  className="btn btn--primary btn--md btn--rounded"
                  onClick={() => setShowGarminSyncResultModal(false)}
                >
                  Got it
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Health Data Consent Modal */}
      {showConsentModal && (
        <div className="modal-overlay" onClick={(e) => {
          // Don't close on overlay click - user must explicitly choose
          e.stopPropagation();
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h3>Health Data Consent</h3>
            </div>
            <div className="modal-body" style={{ padding: '24px' }}>
              <p style={{ marginBottom: '20px', fontSize: '14px', lineHeight: '1.6', color: 'var(--color-text-primary)' }}>
                I explicitly consent to the processing of my health and biometric data, including wearable data and laboratory biomarkers (e.g. hormone levels), to provide personalised health insights and reports.
              </p>
              <p style={{ marginBottom: '20px', fontSize: '14px', lineHeight: '1.6', color: 'var(--color-text-secondary)', fontStyle: 'italic' }}>
                You are unable to {pendingAction === 'manualEntry' ? 'add sleep data' : 'connect wearable devices'} without clicking the I consent button.
              </p>
              <p style={{ marginBottom: '20px', fontSize: '14px', lineHeight: '1.6', color: 'var(--color-text-secondary)', fontStyle: 'italic' }}>
                You can withdraw this consent at any time in account settings.
              </p>
              <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
                <button
                  type="button"
                  className="btn btn--secondary btn--md btn--rounded"
                  onClick={handleNoConsent}
                  style={{ flex: 1 }}
                >
                  I don't consent
                </button>
                <button
                  type="button"
                  className="btn btn--primary btn--md btn--rounded"
                  onClick={handleConsent}
                  style={{ flex: 1 }}
                >
                  I consent
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default WearablesTab;

